// @system — OG / social meta tags injector for the document <head> (launch contract rules 6 + 7)
// @custom — Pass your own props per page; falls back to the brand defaults from info (brand.json)
//
// Usage:
//   <OgMeta title="My Page" description="..." image="https://..." />
//
// Props all optional — omit to use the product defaults: title = info.name, description =
// info.description / info.tagline, image = the brand OG image (assets/og/og-image.png, the same
// file client/index.html advertises), twitter:card = summary_large_image. Every public route gets
// these defaults from routes/@system/AppRoutes.jsx; a page that renders its own <OgMeta> wins
// (its effect runs after the route-level one).
import { useEffect } from 'react'
import { info } from '@/config'

/** Repo path assets/og/og-image.png, served at /assets/og/… — matches client/index.html og:image. */
export const DEFAULT_OG_IMAGE_PATH = '/assets/og/og-image.png'

/**
 * @param {string} property
 * @param {string} content
 * @param {'property' | 'name'} [attr]
 */
function setMeta(property, content, attr = 'property') {
  let el = document.querySelector(`meta[${attr}="${property}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, property)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

/**
 * @param {string} property
 * @param {'property' | 'name'} [attr]
 */
function removeMeta(property, attr = 'property') {
  const el = document.querySelector(`meta[${attr}="${property}"]`)
  if (el) el.remove()
}

/**
 * Absolute URL of the default OG image on the ORIGIN THAT SERVED THE PAGE — not info.url, which a
 * product often leaves at its dev value (crawlers and the launch check must be able to fetch it).
 */
export function defaultOgImage() {
  return `${window.location.origin}${DEFAULT_OG_IMAGE_PATH}`
}

/**
 * Brand default description for the search snippet: info.metaDescription (brand description
 * clamped to ≤ 160 characters by apply-brand — rule 6), then the full description, then the tagline.
 */
export function defaultDescription() {
  for (const candidate of [info.metaDescription, info.description, info.tagline]) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate
  }
  return ''
}

/**
 * @typedef {object} OgMetaProps
 * @property {string} [title] - Falls back to info.name
 * @property {string} [description] - Falls back to info.description / info.tagline
 * @property {string} [image] - Absolute image URL; falls back to the brand OG image
 * @property {string} [url] - Canonical URL (defaults to the current location)
 * @property {string} [type='website'] - og:type
 * @property {string} [twitterCard='summary_large_image']
 */

/** @param {OgMetaProps} props */
export function OgMeta({
  title,
  description,
  image,
  url,
  type = 'website',
  twitterCard = 'summary_large_image' }) {
  useEffect(() => {
    const resolvedTitle = title ?? info.name
    const resolvedUrl = url ?? (window.location.origin + window.location.pathname)
    const resolvedDescription = description ?? defaultDescription()
    const resolvedImage = image ?? defaultOgImage()

    // <title>
    document.title = resolvedTitle

    // standard meta
    if (resolvedDescription) setMeta('description', resolvedDescription, 'name')

    // Open Graph
    setMeta('og:type', type)
    setMeta('og:title', resolvedTitle)
    setMeta('og:url', resolvedUrl)
    if (resolvedDescription) setMeta('og:description', resolvedDescription)
    setMeta('og:image', resolvedImage)

    // Twitter / X
    setMeta('twitter:card', twitterCard, 'name')
    setMeta('twitter:title', resolvedTitle, 'name')
    setMeta('twitter:url', resolvedUrl, 'name')
    if (resolvedDescription) setMeta('twitter:description', resolvedDescription, 'name')
    setMeta('twitter:image', resolvedImage, 'name')

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', resolvedUrl)

    return () => {
      // Restore title on unmount
      document.title = info.name
      // Remove tags that may bleed across pages
      removeMeta('og:type')
      removeMeta('og:title')
      removeMeta('og:url')
      removeMeta('og:description')
      removeMeta('og:image')
      removeMeta('twitter:card', 'name')
      removeMeta('twitter:title', 'name')
      removeMeta('twitter:url', 'name')
      removeMeta('twitter:description', 'name')
      removeMeta('twitter:image', 'name')
    }
  }, [title, description, image, url, type, twitterCard])

  return null
}
